type Translate = (key: string, options?: Record<string, unknown>) => string;

const AUTH_ERROR_ALIASES: Record<string, string> = {
  invalid_credentials: 'Invalid login credentials',
  email_not_confirmed: 'Email not confirmed',
  email_exists: 'duplicateEmailError',
  user_already_exists: 'User already registered',
  user_not_found: 'Invalid login credentials',
  'Signups not allowed for otp': 'emailOtpUnavailable',
};

export function getAuthErrorKey(error: unknown): string {
  let code = '';

  if (typeof error === 'string') {
    code = error;
  } else if (error && typeof error === 'object') {
    if ('code' in error && typeof error.code === 'string' && error.code) {
      code = error.code;
    } else if ('message' in error && typeof error.message === 'string') {
      code = error.message;
    }
  }

  return Object.hasOwn(AUTH_ERROR_ALIASES, code)
    ? (AUTH_ERROR_ALIASES[code] ?? code)
    : code;
}

/**
 * Map a Supabase auth error to a user-facing message, mirroring the web
 * AuthErrorAlert which keys translations off the error message/code
 * (e.g. "Invalid login credentials", "Email not confirmed", "same_password").
 *
 * Native toasts can't render the embedded link components the web alert uses
 * (<ResetPasswordLink/> etc.), so any markup tags in the resolved string are
 * stripped. When the code has no specific translation, the provided fallback
 * is returned.
 *
 * @param error    The thrown provider error or legacy message string.
 * @param t        i18next translate function.
 * @param fallback Translation key used when no specific mapping exists.
 *                 Defaults to the generic "couldn't authenticate" message.
 */
export function resolveAuthErrorMessage(
  error: unknown,
  t: Translate,
  fallback = 'auth:errors.generic',
): string {
  const fallbackMessage = t(fallback);
  const code = getAuthErrorKey(error);

  if (!code) return fallbackMessage;

  const translated = t(`auth:errors.${code}`, { defaultValue: '' });

  if (!translated) return fallbackMessage;

  // Strip any link/markup tags a toast can't render (e.g. <ResetPasswordLink>).
  const cleaned = translated.replace(/<\/?[^>]+>/g, '').trim();

  return cleaned || fallbackMessage;
}

/**
 * Set of GoTrue error codes that are safe to surface on the password-reset
 * request screen. The reset flow must NOT confirm whether an email exists
 * (enumeration), so only operational errors like rate-limiting are shown;
 * everything else falls through to the generic message.
 */
const RESET_SAFE_CODES = new Set([
  'over_email_send_rate_limit',
  'over_request_rate_limit',
  'email_provider_disabled',
  'email_address_invalid',
  'captcha_failed',
]);

/**
 * Enumeration-safe variant for the password-reset request screen: only reveals
 * operational errors (rate limits, provider disabled). For any other code —
 * including ones that would imply whether the email exists — it returns the
 * generic message.
 */
export function resolveResetRequestErrorMessage(
  error: unknown,
  t: Translate,
): string {
  const code = getAuthErrorKey(error);

  if (RESET_SAFE_CODES.has(code)) {
    return resolveAuthErrorMessage(error, t);
  }

  return t('auth:errors.emailLinkRequestFailed');
}

/**
 * Identify if an error is a standard network blip (offline, DNS failure, etc)
 * typical of fetch() failures in Safari/WebKit on mobile.
 */
export function isNetworkBlip(error: unknown): boolean {
  if (!(error instanceof Error)) return false;

  // Only target the exact browser network drop exceptions
  if (error.name !== 'TypeError' && error.name !== 'AuthRetryableFetchError') {
    return false;
  }

  const msg = error.message;
  return (
    msg === 'Load failed' ||
    msg.startsWith('Load failed (') || // Supabase sometimes appends the domain
    msg === 'Failed to fetch' ||
    msg === 'Network request failed' ||
    msg.includes('NetworkError')
  );
}
